import { CheckOutlined, CloseSquareTwoTone, MinusSquareFilled } from '@ant-design/icons';
import { Button, Card, Col, Descriptions, Divider, Row, Space, Steps, Typography } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Outlet } from 'react-router-dom';

import useAPIQuery from '../../api/useAPIQuery';
import ApproveRequestOrStageModal from '../../components/ApproveRequestOrStageModal';
import ErrorResult from '../../components/ErrorResult';
import PageContent from '../../components/PageContent';
import RejectRequestJobPostingModal from '../../components/RejectRequestJobPostingModal';
import RequestJobPostingTabs from '../../components/RequestJobPostingTabs/RequestJobPostingTabs';
import usePermissionCheckerQuery from '../../hooks/usePermissionCheckerQuery';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { RequestJobPostingId, RequestJobPostingStageWithApprover } from '../../types';
import styles from './RequestJobPostingAnalysis.module.css';

const RequestJobPostingAnalysis = () => {
  const { id } = useTypedParams({ id: RequestJobPostingId });
  const [openApproveRequestOrStage, setOpenApproveRequestOrStage] = useState(false);
  const [openRejectRequestJobPosting, setOpenRejectRequestJobPosting] = useState(false);

  const user = useAPIQuery('/users/me/');

  const hasApproveRequestJobPostingsPermission = usePermissionCheckerQuery([
    'can_approve_request_job_postings',
  ]);

  const requestJobPosting = useAPIQuery(`/request-job-postings/${id}/`);

  const currentUserStageInProgress = requestJobPosting.data?.stages.find(
    (stage) => stage.status === 'IN_PROGRESS'
  );

  const lastStage =
    (requestJobPosting.data?.stages.length ?? 0) - 1 === currentUserStageInProgress?.sequence;

  return (
    <>
      {openApproveRequestOrStage && (
        <ApproveRequestOrStageModal
          requestJobPostingId={id}
          currentStage={currentUserStageInProgress as RequestJobPostingStageWithApprover}
          isLastStage={lastStage}
          open={openApproveRequestOrStage}
          onCancel={() => setOpenApproveRequestOrStage(false)}
          onSuccess={() => {
            setOpenApproveRequestOrStage(false);
            requestJobPosting.refetch();
          }}
          destroyOnClose
        />
      )}
      {openRejectRequestJobPosting && (
        <RejectRequestJobPostingModal
          requestJobPostingId={id}
          currentStage={currentUserStageInProgress as RequestJobPostingStageWithApprover}
          open={openRejectRequestJobPosting}
          onCancel={() => setOpenRejectRequestJobPosting(false)}
          onSuccess={() => {
            setOpenRejectRequestJobPosting(false);
            requestJobPosting.refetch();
          }}
          destroyOnClose
        />
      )}
      <ErrorResult error={requestJobPosting.error}>
        <Helmet>
          <title>
            Analisar requisição | {requestJobPosting.data?.name || '...'} | Recrutamento e Seleção
          </title>
        </Helmet>
        <PageContent>
          <Outlet />
          <Card data-testid="main-card">
            <Space direction="vertical" size="middle">
              <Col span={24}>
                <RequestJobPostingTabs />
              </Col>
              <Row>
                <Col span={11}>
                  <Typography.Title level={4} style={{ color: '#166889' }}>
                    Fluxo
                  </Typography.Title>
                  <Steps
                    className={[styles.stepItemIcon, styles.stepItemIconSvg].join(' ')}
                    current={
                      requestJobPosting.data?.status === 'APPROVED'
                        ? requestJobPosting.data.stages.length + 1
                        : requestJobPosting.data?.status === 'REJECTED'
                        ? requestJobPosting.data?.stages.find(
                            (stage) => stage.status === 'REJECTED'
                          )?.sequence || undefined
                        : requestJobPosting.data?.stages.find(
                            (stage) => stage.status === 'IN_PROGRESS'
                          )?.sequence || undefined
                    }
                    direction="vertical"
                    items={requestJobPosting.data?.stages.map((stage) => ({
                      title: stage.name,
                      description: stage.approver.full_name,
                      icon:
                        stage.status === 'REJECTED' ? (
                          <CloseSquareTwoTone
                            twoToneColor="#f51505"
                            style={{ width: '100%', height: '100%' }}
                          />
                        ) : undefined,
                    }))}
                  />
                </Col>
                <Col span={1}>
                  <Divider type="vertical" style={{ height: '100%' }} />
                </Col>
                <Col span={11}>
                  <Descriptions
                    title={
                      <Typography.Title level={4} style={{ color: '#166889' }}>
                        Dados
                      </Typography.Title>
                    }
                    column={1}
                    labelStyle={{ color: 'var(--ant-primary-color)' }}
                  >
                    <Descriptions.Item label="Modelo de requisição">
                      {requestJobPosting.data?.template_name}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tipo de requisição">
                      {requestJobPosting.data?.type === 'STAFF_INCREASE'
                        ? 'Aumento de Quadro'
                        : 'Substituição'}
                    </Descriptions.Item>
                    {requestJobPosting.data?.type === 'SUBSTITUTION' && (
                      <Descriptions.Item label="Nome do substituído">
                        {requestJobPosting.data.employee_name_being_replaced || '-'}
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Nome da requisição">
                      {requestJobPosting.data?.name}
                    </Descriptions.Item>
                    <Descriptions.Item label="Quantidade de posições">
                      {requestJobPosting.data?.positions}
                    </Descriptions.Item>
                    <Descriptions.Item label="Faixa salarial">
                      {requestJobPosting.data?.start_salary && requestJobPosting.data?.end_salary
                        ? `R$ ${requestJobPosting.data?.start_salary} a R$ ${requestJobPosting.data?.end_salary}`
                        : requestJobPosting.data?.end_salary &&
                          !!requestJobPosting.data?.start_salary
                        ? `R$ ${requestJobPosting.data.end_salary}`
                        : 'Sem faixa salarial'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Unidade">
                      {requestJobPosting.data?.branch || '-'}
                    </Descriptions.Item>
                  </Descriptions>
                  <Row>
                    {currentUserStageInProgress &&
                    (user.data?.is_staff ||
                      (currentUserStageInProgress.approver.id === user.data?.id &&
                        hasApproveRequestJobPostingsPermission)) ? (
                      <Col span={2} offset={16}>
                        <Space>
                          <Button
                            type="primary"
                            danger
                            icon={<MinusSquareFilled />}
                            disabled={
                              !['NOT_INITIATED', 'IN_PROGRESS'].includes(
                                requestJobPosting.data?.status || ''
                              )
                            }
                            onClick={() => {
                              setOpenRejectRequestJobPosting(true);
                            }}
                          >
                            Reprovar
                          </Button>
                          <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            disabled={
                              !['NOT_INITIATED', 'IN_PROGRESS'].includes(
                                requestJobPosting.data?.status || ''
                              )
                            }
                            onClick={() => {
                              setOpenApproveRequestOrStage(true);
                            }}
                          >
                            Aprovar
                          </Button>
                        </Space>
                      </Col>
                    ) : null}
                  </Row>
                </Col>
              </Row>
            </Space>
          </Card>
        </PageContent>
      </ErrorResult>
    </>
  );
};

export default withRouteChange()(RequestJobPostingAnalysis);
